/**
 * Submit Intake Forms Actor
 *
 * Submits all completed intake forms to the backend by calling:
 * 1. saveIntakeFormResponse - saves form responses
 * 2. saveIntakePdfToEhr - generates and submits PDF to EHR
 *
 * Both operations run in parallel per form, with independent error handling.
 * Each operation has configurable retry logic with exponential backoff.
 */

import { formatResponseForCopy } from "@/app/directories/checkout/components/intake/IntakeFormQuestion";
import { saveIntakePdfToEhr } from "@/app/directories/checkout/components/intake/intakeHelper";
import { datadogLogs } from "@datadog/browser-logs";
import { saveIntakeFormResponse } from "@frontend/lib/actions/actions";
import { formatInTimeZone } from "date-fns-tz";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getIntakeFormsAndQuestionsByFormIds } from "../../server-actions/intake-actions";
import type { ActorWithMetadata } from "./send-sms.actor";

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Retry a promise-returning function with exponential backoff
 *
 * @param fn - Function that returns a promise
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param initialDelayMs - Initial delay in milliseconds before first retry (default: 1000)
 * @param maxDelayMs - Maximum delay in milliseconds (default: 10000)
 * @param backoffMultiplier - Multiplier for exponential backoff (default: 2)
 * @returns Promise that resolves with the function result or rejects after all retries fail
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  initialDelayMs = 1000,
  maxDelayMs = 10000,
  backoffMultiplier = 2,
): Promise<T> {
  let lastError: unknown;
  let currentDelay = initialDelayMs;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't sleep after the last attempt
      if (attempt < maxRetries) {
        // Wait before retrying with exponential backoff
        await sleep(Math.min(currentDelay, maxDelayMs));
        currentDelay *= backoffMultiplier;
      }
    }
  }

  // All retries exhausted, throw the last error
  throw lastError;
}

/**
 * Input Schema (What this actor needs)
 */
const SubmitIntakeFormsInputSchema = z.object({
  formIds: z.array(z.number()),
  intakeFormSections: z.array(
    z.object({
      formId: z.string(),
      sectionName: z.string(),
      sectionText: z.string(),
      priority: z.number(),
      sectionTranslations: z.array(z.any()),
      questions: z.array(z.any()),
    }),
  ),
  intakeResponses: z.record(z.unknown()),
  appointmentId: z.string(),
  consumerId: z.string(),
  userId: z.string(),
  orgId: z.number(),
  practiceId: z.number(),
  language: z.string().default("en"),
  consumer: z.object({
    firstname: z.string(),
    lastname: z.string(),
    dob: z.string(),
    ehrPatientId: z.string().optional(),
    ehrEnterpriseId: z.string().optional(),
  }),
  // Retry configuration (optional, with defaults)
  maxRetries: z.number().default(3).optional(),
  retryDelayMs: z.number().default(1000).optional(),
  maxRetryDelayMs: z.number().default(10000).optional(),
});

type SubmitIntakeFormsInput = z.infer<typeof SubmitIntakeFormsInputSchema>;

/**
 * Success Schema (What this actor returns on success)
 *
 * `skipped` is intentionally optional for forward compatibility: the
 * kiosk machine only persists `context` and `value` via
 * `state-serializer.ts` — actor results (including this schema) are
 * ephemeral Promise outputs that don't cross a persistence boundary,
 * so there's no pre-PLA-1308 snapshot that could wrongly satisfy
 * `responseSaved + pdfSaved` after a deploy.
 */
const FormSubmissionResultSchema = z.object({
  formId: z.number(),
  formName: z.string(),
  responseSaved: z.boolean(),
  pdfSaved: z.boolean(),
  skipped: z.boolean().optional(),
  errors: z
    .object({
      responseError: z.string().optional(),
      pdfError: z.string().optional(),
    })
    .optional(),
});

const SubmitIntakeFormsSuccessSchema = z.object({
  results: z.array(FormSubmissionResultSchema),
});

type SubmitIntakeFormsSuccess = z.infer<typeof SubmitIntakeFormsSuccessSchema>;

/**
 * Error Schema (What this actor returns on failure)
 */
const SubmitIntakeFormsErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "MISSING_DATA",
    "SUBMISSION_ERROR",
    "NETWORK_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  formErrors: z
    .array(
      z.object({
        formId: z.number(),
        error: z.string(),
      }),
    )
    .optional(),
});

type SubmitIntakeFormsError = z.infer<typeof SubmitIntakeFormsErrorSchema>;

/**
 * Format answers for a form (similar to TypeformIntake.tsx formatAnswers)
 */
function formatAnswers(
  data: Record<string, unknown>,
  questions: Array<{
    intakeQuestion: {
      id: number;
      questionText: string;
      questionNumber: number;
      type: string;
    };
  }>,
): Record<string, unknown> {
  return Object.entries(data).reduce(
    (accum, [key, value]) => {
      const originalQuestion = questions.find(
        (q) => q.intakeQuestion.id.toString() === key,
      );

      if (!originalQuestion || !value) {
        return accum;
      }

      const richResponse =
        originalQuestion.intakeQuestion.type === "SIGNATURE"
          ? {
              signature: (value as { signature?: unknown }).signature,
              ...value,
            }
          : null;

      const formattedResponse = (() => {
        switch (originalQuestion.intakeQuestion.type) {
          case "SIGNATURE":
            return `signed on ${(value as { date?: string }).date || ""}`;
          default:
            return typeof value === "string" ? value : JSON.stringify(value);
        }
      })();

      accum[key] = {
        questionText: originalQuestion.intakeQuestion.questionText,
        questionNumber: originalQuestion.intakeQuestion.questionNumber,
        response: formattedResponse,
        richResponse: richResponse,
      };

      return accum;
    },
    {} as Record<string, unknown>,
  );
}

/**
 * Build allQuestionsForPdf array (similar to TypeformIntake.tsx)
 */
function buildAllQuestionsForPdf(
  formSections: Array<{
    formId: string;
    questions: Array<{
      section: string;
      intakeQuestion: {
        id: number;
        type: string;
        questionText: string;
        originalText?: string;
        questionAttributes: {
          choices?: Array<{ id: string; value: string }>;
        };
        originalAttributes?: {
          choices?: Array<{ id: string; value: string }>;
        };
        superscriptIntakeQuestion?: {
          id: number;
        };
        intakeFormSection?: {
          intakeFormSectionTranslations?: Array<{
            languageCode: string;
            text: string;
          }>;
        };
      };
    }>;
  }>,
  formId: number,
  intakeResponses: Record<string, unknown>,
  language: string,
): Array<unknown> {
  const sectionsForForm = formSections.filter(
    (s) => s.formId === formId.toString(),
  );

  return sectionsForForm.flatMap((formSection) =>
    formSection.questions
      .filter(
        (question) =>
          question.intakeQuestion &&
          intakeResponses[question.intakeQuestion.id.toString()],
      )
      .map((question) => {
        const response = intakeResponses[question.intakeQuestion.id.toString()];

        if (question.intakeQuestion.type === "SIGNATURE") {
          const signature = response as {
            signature?: { dataUrl?: string; text?: string; style?: string };
            date?: string;
          };
          return {
            id: question.intakeQuestion.id,
            originalSectionText: question.section,
            type: question.intakeQuestion.type,
            translatedSectionText:
              question.intakeQuestion.intakeFormSection?.intakeFormSectionTranslations?.find(
                (section) => section.languageCode === language,
              )?.text || "",
            questionText: question.intakeQuestion.questionText,
            originalQuestionText: question.intakeQuestion.originalText || "",
            questionAttributes: question.intakeQuestion.questionAttributes,
            originalQuestionAttributes:
              question.intakeQuestion.originalAttributes || {},
            responseCopy: "",
            signature: {
              url: signature.signature?.dataUrl || "",
              text: signature.signature?.text || "",
              fontStyle: signature.signature?.style || "",
              blurb:
                (
                  question.intakeQuestion.originalAttributes as {
                    blurb?: string;
                  }
                ).blurb || null,
              date: signature.date || "",
            },
            responseCreatedAt: signature.date || "",
          };
        }

        const originalChoicesWithIds =
          question.intakeQuestion.type === "SINGLE_SELECT"
            ? (
                question.intakeQuestion.originalAttributes as {
                  choices?: Array<{ id: string; value: string }>;
                }
              )?.choices?.map((choice, index) => ({
                ...choice,
                id: index.toString(),
              }))
            : undefined;

        const questionChoicesWithIds =
          question.intakeQuestion.type === "SINGLE_SELECT"
            ? (
                question.intakeQuestion.questionAttributes as {
                  choices?: Array<{ id: string; value: string }>;
                }
              )?.choices?.map((choice, index) => ({
                ...choice,
                id: index.toString(),
              }))
            : undefined;

        const originalQuestionAttributes =
          question.intakeQuestion.type === "SINGLE_SELECT"
            ? {
                ...(question.intakeQuestion.originalAttributes as Record<
                  string,
                  unknown
                >),
                choices: originalChoicesWithIds,
              }
            : question.intakeQuestion.originalAttributes || {};

        const questionAttributes =
          question.intakeQuestion.type === "SINGLE_SELECT"
            ? {
                ...(question.intakeQuestion.questionAttributes as Record<
                  string,
                  unknown
                >),
                choices: questionChoicesWithIds,
              }
            : question.intakeQuestion.questionAttributes || {};

        if (question.intakeQuestion.superscriptIntakeQuestion?.id === 18) {
          questionAttributes.choices = [
            { id: "0", value: "Male" },
            { id: "1", value: "Female" },
            { id: "2", value: "Unknown" },
          ];
          originalQuestionAttributes.choices = [
            { id: "0", value: "Male" },
            { id: "1", value: "Female" },
            { id: "2", value: "Unknown" },
          ];
        }
        return {
          id: question.intakeQuestion.id,
          originalSectionText: question.section,
          translatedSectionText:
            question.intakeQuestion.intakeFormSection?.intakeFormSectionTranslations?.find(
              (section) => section.languageCode === language,
            )?.text || "",
          type: question.intakeQuestion.type,
          questionText: question.intakeQuestion.questionText,
          originalQuestionText: question.intakeQuestion.originalText || "",
          questionAttributes: questionAttributes,
          originalQuestionAttributes: originalQuestionAttributes,
          responseCopy: formatResponseForCopy(
            question.intakeQuestion as never,
            response,
            language === "en",
          ),
          responseCreatedAt: formatInTimeZone(
            new Date(),
            Intl.DateTimeFormat().resolvedOptions().timeZone,
            "MM/dd/yyyy hh:mm aa",
          ),
        };
      }),
  );
}

/**
 * Actor Implementation
 */
const submitIntakeFormsActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: SubmitIntakeFormsInput;
  }): Promise<SubmitIntakeFormsSuccess> => {
    // Validate input
    const validated = SubmitIntakeFormsInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: SubmitIntakeFormsError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    if (
      !validated.data.consumer.ehrPatientId &&
      !validated.data.consumer.ehrEnterpriseId
    ) {
      const errorData: SubmitIntakeFormsError = {
        code: "MISSING_DATA",
        message:
          "EHR patient ID or EHR enterprise ID is required to submit intake forms",
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    const {
      formIds,
      intakeFormSections,
      intakeResponses,
      appointmentId,
      consumerId,
      userId,
      orgId,
      practiceId,
      language,
      consumer,
      maxRetries = 3,
      retryDelayMs = 1000,
      maxRetryDelayMs = 10000,
    } = validated.data;

    // Check if we have forms to process
    if (!formIds || formIds.length === 0) {
      return {
        results: [],
      };
    }

    // Check if we have appointmentId
    if (!appointmentId) {
      const errorData: SubmitIntakeFormsError = {
        code: "MISSING_DATA",
        message: "Appointment ID is required to submit intake forms",
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Check if we have responses
    if (!intakeResponses || Object.keys(intakeResponses).length === 0) {
      const errorData: SubmitIntakeFormsError = {
        code: "MISSING_DATA",
        message: "No intake responses found to submit",
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    datadogLogs.logger.info(
      `[submitIntakeFormsActor] Submitting intake forms for consumer ${consumerId}`,
      {
        formIds,
        appointmentId,
        practiceId,
      },
    );

    try {
      // Fetch form metadata to get form names - might be able to get this
      // from the intakeFormSections array or context instead of making a separate API call

      const intakeForms = await getIntakeFormsAndQuestionsByFormIds(formIds);

      // Process all forms in parallel
      const submissionPromises = formIds.map(async (formId) => {
        try {
          const form = intakeForms.find((f) => f.id === formId);
          const formName = form?.name?.trim() || `Form ${formId}`;
          // Get all questions for this form from sections
          const allQuestionsForForm = intakeFormSections
            .filter((section) => section.formId === formId.toString())
            .flatMap((section) => section.questions);

          // Filter responses for this form
          const formResponses: Record<string, unknown> = {};
          for (const question of allQuestionsForForm) {
            const questionId = question.intakeQuestion.id.toString();
            if (intakeResponses[questionId]) {
              formResponses[questionId] = intakeResponses[questionId];
            }
          }

          // Skip forms the patient didn't fill out (expected for returning patients)
          if (Object.keys(formResponses).length === 0) {
            datadogLogs.logger.info(
              `[submitIntakeFormsActor] Skipping ${formName} for consumer ${consumerId} — no responses`,
            );
            return {
              formId,
              formName,
              responseSaved: false,
              pdfSaved: false,
              skipped: true,
            };
          }

          // Format responses
          const formattedAnswers = formatAnswers(
            formResponses,
            allQuestionsForForm,
          );

          // Build allQuestionsForPdf
          const allQuestionsForPdf = buildAllQuestionsForPdf(
            intakeFormSections,
            formId,
            intakeResponses,
            language,
          );

          // Submit both operations in parallel with retry logic
          const [responseResult, pdfResult] = await Promise.allSettled([
            retryWithBackoff(
              () =>
                saveIntakeFormResponse(
                  formName,
                  formId,
                  appointmentId,
                  consumerId,
                  userId,
                  formattedAnswers,
                  true, // isComplete
                  orgId,
                  form?.consentTypes?.length ? form?.consentTypes : undefined,
                  true, // fromKiosk
                ),
              maxRetries,
              retryDelayMs,
              maxRetryDelayMs,
            ),
            // this function already has retry logic internally
            saveIntakePdfToEhr(
              formResponses,
              formId,
              consumer,
              formName,
              appointmentId,
              language,
              allQuestionsForPdf,
              orgId,
              userId,
              practiceId,
              undefined, // visitData - no need to add this
              undefined, // logo - should add this tho
            ),
            maxRetries,
            retryDelayMs,
            maxRetryDelayMs,
          ]);

          const result: z.infer<typeof FormSubmissionResultSchema> = {
            formId,
            formName,
            responseSaved: responseResult.status === "fulfilled",
            pdfSaved: pdfResult.status === "fulfilled",
          };

          // Collect errors if any
          const errors: {
            responseError?: string;
            pdfError?: string;
          } = {};

          if (responseResult.status === "rejected") {
            errors.responseError =
              responseResult.reason instanceof Error
                ? responseResult.reason.message
                : String(responseResult.reason);
          }

          if (pdfResult.status === "rejected") {
            errors.pdfError =
              pdfResult.reason instanceof Error
                ? pdfResult.reason.message
                : String(pdfResult.reason);
          }

          if (Object.keys(errors).length > 0) {
            result.errors = errors;
          }

          return result;
        } catch (error) {
          // If the entire form processing fails (e.g., during setup), return error result
          const form = intakeForms.find((f) => f.id === formId);
          return {
            formId,
            formName: form?.name?.trim() || `Form ${formId}`,
            responseSaved: false,
            pdfSaved: false,
            errors: {
              responseError:
                error instanceof Error ? error.message : String(error),
            },
          };
        }
      });

      // Wait for all forms to complete (parallel execution)
      const settledResults = await Promise.allSettled(submissionPromises);

      // Extract results, handling any form-level failures
      const results = settledResults.map((settled, index) => {
        if (settled.status === "fulfilled") {
          return settled.value;
        }
        // If a form's processing failed entirely, return error result
        const formId = formIds[index];
        const form = intakeForms.find((f) => f.id === formId);
        return {
          formId,
          formName: form?.name || `Form ${formId}`,
          responseSaved: false,
          pdfSaved: false,
          errors: {
            responseError:
              settled.reason instanceof Error
                ? settled.reason.message
                : String(settled.reason),
          },
        };
      });

      for (const result of results) {
        if (result.skipped) {
          continue;
        }
        if (result.errors || !result.pdfSaved || !result.responseSaved) {
          datadogLogs.logger.error(
            `[submitIntakeFormsActor] Form submission failed for ${result.formName} for consumer ${consumerId}`,
            result,
          );
        } else {
          datadogLogs.logger.info(
            `[submitIntakeFormsActor] Form submission succeeeded for ${result.formName} for consumer ${consumerId}`,
          );
        }
      }

      return {
        results,
      };
    } catch (error) {
      // Re-throw typed errors
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        "message" in error
      ) {
        throw error;
      }

      // Wrap unknown errors
      const wrappedError: SubmitIntakeFormsError = {
        code: "NETWORK_ERROR",
        message: error instanceof Error ? error.message : "Network error",
        retryable: true,
      };
      const errorObj = new Error(wrappedError.message);
      Object.assign(errorObj, wrappedError);
      throw errorObj;
    }
  },
);

/**
 * Submit Intake Forms Actor with Metadata
 */
export const submitIntakeFormsActor: ActorWithMetadata = {
  impl: submitIntakeFormsActorImpl,
  metadata: {
    displayName: "submitIntakeForms",
    description:
      "Submit all completed intake forms (save responses and PDFs to EHR)",
    category: "data",
    retryable: true,
    timeout: 60000, // 60 seconds (multiple API calls)
    errorCodes: [
      "INVALID_INPUT",
      "MISSING_DATA",
      "SUBMISSION_ERROR",
      "NETWORK_ERROR",
    ],
    inputSchema: SubmitIntakeFormsInputSchema,
    successSchema: SubmitIntakeFormsSuccessSchema,
    errorSchema: SubmitIntakeFormsErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as SubmitIntakeFormsSuccess;
      return {
        intakeSubmissionResults: success.results,
      };
    },
    onError: (error: unknown) => {
      const err = error as SubmitIntakeFormsError;
      return {
        intakeSubmissionError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
          formErrors: err.formErrors,
        },
      };
    },
  },
};
